/**
 * Normalize distance name for comparison
 * - Trim whitespace
 * - Convert to lowercase
 * - Replace multiple spaces with single space
 * - Remove special characters (except dots and commas)
 * 
 * NOTE: This function must match the backend implementation exactly!
 */
export function normalizeDistanceName(name) {
  if (!name) return ''
  
  return name
    .trim()                          // Remove leading/trailing whitespace
    .toLowerCase()                   // Case insensitive
    .replace(/\s+/g, ' ')            // Multiple spaces → single space
    .replace(/[^\w\s\.\,а-яёА-ЯЁ]/g, '') // Remove special chars, keep cyrillic, dots, commas
}

/**
 * Map distance name to distance object using smart matching
 * 
 * @param {string} distanceName - The distance name from participant data
 * @param {Array} availableDistances - Array of distance objects with {id, name}
 * @returns {Object} - {distanceId, distanceName, matched}
 */
export function mapDistanceNameToId(distanceName, availableDistances) {
  if (!distanceName || !availableDistances || availableDistances.length === 0) {
    return {
      distanceId: null,
      distanceName: distanceName,
      matched: false
    }
  }

  const normalized = normalizeDistanceName(distanceName)

  for (const distance of availableDistances) {
    if (normalizeDistanceName(distance.name) === normalized) {
      return {
        distanceId: distance.id,
        distanceName: distance.name,
        matched: true,
        originalDistance: distance
      }
    }
  }

  return {
    distanceId: null,
    distanceName: distanceName,
    matched: false
  }
}

/**
 * Validate and map distances for all participants
 * 
 * @param {Array} participants - Array of participant objects
 * @param {Array} availableDistances - Array of distance objects
 * @returns {Object} - Validation result with counts and lists
 */
export function validateParticipantsDistances(participants, availableDistances) {
  if (!participants || participants.length === 0) {
    return {
      valid: true,
      invalidCount: 0,
      invalidPercentage: 0,
      invalidParticipants: [],
      mappedParticipants: [],
      totalParticipants: 0
    }
  }

  const results = participants.map(p => {
    const mapping = mapDistanceNameToId(p.distance, availableDistances)
    return {
      ...p,
      distanceId: mapping.distanceId,
      matchedDistance: mapping.originalDistance,
      originalDistanceName: p.distance,
      distanceMatched: mapping.matched
    }
  })

  const invalid = results.filter(r => !r.distanceId)
  const valid = results.filter(r => r.distanceId)

  return {
    valid: invalid.length === 0,
    invalidCount: invalid.length,
    invalidPercentage: (invalid.length / participants.length) * 100,
    invalidParticipants: invalid,
    mappedParticipants: valid,
    totalParticipants: participants.length
  }
}

